import { NoteDto } from '@maia/shared';
import {
  Divider,
  FeaturedIcon,
  MarkdownRenderer,
  SideDrawer,
  SidedrawerProps,
  Spec,
  SpecList,
  Typography,
  UploadedFile,
} from '@maia/ui';
import { useDrawerStore } from './../../../stores/drawer.store';
import { mapper } from '@maia/icons';
import { downloadEncodedDocument, formatDate, getFullName } from '@maia/utils';
import { useId } from 'react';
import { useMutation } from '@tanstack/react-query';
import { service } from '@maia/attachments';

type Props = {
  note?: NoteDto;
} & Pick<SidedrawerProps, 'onOpenChange' | 'portalRef'>;

export function NoteSidedrawer(props: Props) {
  const id = useId();
  const { note, ...rest } = props;
  const { state, closeDrawer } = useDrawerStore();

  const { mutateAsync } = useMutation({
    mutationFn: (guid: string) => service.find(guid),
  });

  async function handleDownload(guid: string) {
    const documentData = await mutateAsync(guid);

    if (documentData) {
      return downloadEncodedDocument(documentData);
    }
  }

  return (
    <SideDrawer
      {...rest}
      title="Bekijk notitie"
      className="w-full pb-32 max-w-[var(--max-side-drawer-width)]"
      isOpen={state?.isOpen && state.info?.type === 'note'}
      onOpenChange={closeDrawer}
    >
      {state?.info?.type === 'note' ? (
        <div className="flex flex-col mt-32 gap-y-32">
          <div className="flex flex-col">
            <FeaturedIcon
              variant="modern"
              size="sm"
              icon={mapper(state?.info.data.icon)}
            />

            <Typography
              variant="body-s"
              weight="medium"
              className="text-grayscale600 mt-12"
            >
              {state?.info.data.title}
            </Typography>

            <SpecList className="mt-12">
              <Spec
                className="grid grid-cols-2"
                title="Aanmaak datum"
                value={formatDate(state?.info.data.createdAt)}
              />
              <Spec
                className="grid grid-cols-2"
                title="Aangemaakt door"
                value={getFullName(state?.info.data.createdBy)}
              />
            </SpecList>
          </div>

          <Divider />

          <div className="overflow-y-auto max-h-[300px]">
            <MarkdownRenderer markdown={state?.info.data.content} />
          </div>

          <div className="overflow-y-auto max-h-[300px] space-y-8">
            {state?.info.data.attachments.map((attachment, key) => (
              <UploadedFile
                key={`note-sidedrawer-${id}-uploaded-file-${attachment.filename}-${key}`}
                name={attachment.filename}
                mimetype={attachment.mimetype}
                action="download"
                onDownload={async () => await handleDownload(attachment.guid)}
              />
            ))}
          </div>
        </div>
      ) : null}
    </SideDrawer>
  );
}
